import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

const DEFAULTS = {
  page: 1,
  page_size: 20,
  search: '',
};

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/**
 * URL-backed list state for pagination, search, and filters.
 * Refresh / back / forward restore the same view.
 *
 * @param {object} schema - Param defaults. Keys become URL query keys.
 *   Example: { page: 1, page_size: 20, search: '', status: 'all', priority: 'all' }
 * @param {object} options
 * @param {string[]} options.resetPageOn - keys that reset page to 1 when changed
 * @param {boolean} options.replace - use history replace (default true for filter churn)
 */
export function useListUrlState(schema = DEFAULTS, options = {}) {
  const {
    resetPageOn = ['tab', 'search', 'status', 'priority', 'type', 'story', 'suite', 'page_size', 'sort', 'sort_direction'],
    replace = true,
  } = options;

  const [searchParams, setSearchParams] = useSearchParams();
  const defaults = useMemo(() => ({ ...DEFAULTS, ...schema }), [schema]);

  const values = useMemo(() => {
    const next = {};
    for (const [key, def] of Object.entries(defaults)) {
      const raw = searchParams.get(key);
      if (raw == null || raw === '') {
        next[key] = def;
        continue;
      }
      if (typeof def === 'number') {
        const n = Number(raw);
        next[key] = Number.isFinite(n) && n > 0 ? n : def;
      } else {
        next[key] = raw;
      }
    }
    return next;
  }, [searchParams, defaults]);

  const write = useCallback(
    (patch, { resetPage = false, historyReplace = replace } = {}) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const apply = { ...patch };
          if (resetPage) apply.page = 1;

          for (const [key, value] of Object.entries(apply)) {
            const def = defaults[key];
            const isDefault =
              value == null ||
              value === '' ||
              (def !== undefined && String(value) === String(def));

            // Always keep page when > 1; omit defaults to keep URLs clean
            if (key === 'page') {
              const pageNum = Number(value) || 1;
              if (pageNum <= 1) next.delete('page');
              else next.set('page', String(pageNum));
              continue;
            }

            if (isDefault && key !== 'page_size') {
              // Keep non-default page_size; drop default page_size
              if (key === 'page_size' && Number(value) === Number(defaults.page_size)) {
                next.delete(key);
              } else if (key !== 'page_size') {
                next.delete(key);
              } else {
                next.delete(key);
              }
              continue;
            }

            if (isDefault) {
              next.delete(key);
            } else {
              next.set(key, String(value));
            }
          }
          return next;
        },
        { replace: historyReplace }
      );
    },
    [defaults, replace, setSearchParams]
  );

  const setParam = useCallback(
    (key, value) => {
      const shouldReset = resetPageOn.includes(key) && key !== 'page';
      write({ [key]: value }, { resetPage: shouldReset, historyReplace: key === 'page' ? false : replace });
    },
    [resetPageOn, write, replace]
  );

  const setParams = useCallback(
    (patch, opts) => {
      const keys = Object.keys(patch);
      const shouldReset =
        opts?.resetPage ?? keys.some((k) => resetPageOn.includes(k) && k !== 'page');
      write(patch, {
        resetPage: shouldReset,
        historyReplace: opts?.replace ?? replace,
      });
    },
    [resetPageOn, write, replace]
  );

  const setPage = useCallback(
    (page) => write({ page }, { resetPage: false, historyReplace: false }),
    [write]
  );

  const setPageSize = useCallback(
    (page_size) => write({ page_size }, { resetPage: true }),
    [write]
  );

  const setSearch = useCallback(
    (search) => write({ search }, { resetPage: true }),
    [write]
  );

  const clearFilters = useCallback(() => {
    const patch = { page: 1 };
    for (const key of Object.keys(defaults)) {
      if (key === 'page' || key === 'page_size') continue;
      patch[key] = defaults[key];
    }
    write(patch, { resetPage: false });
  }, [defaults, write]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    for (const [key, def] of Object.entries(defaults)) {
      if (key === 'page' || key === 'page_size') continue;
      if (String(values[key] ?? '') !== String(def ?? '')) count += 1;
    }
    return count;
  }, [defaults, values]);

  return {
    ...values,
    values,
    defaults,
    setParam,
    setParams,
    setPage,
    setPageSize,
    setSearch,
    clearFilters,
    activeFilterCount,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
  };
}

export default useListUrlState;
